// A list of tachograph days: each a 24-hour chart with a summary line under it (time per
// activity, distance, number of infringements) that opens to show the infringements in detail.
// Used by a file's page and a driver's page. Times are UTC, as the tachograph records them.
import { useMemo, useState } from 'react';
import { TachoActivity, TachoDay, TachoInfringement, TachoSegment, TachoTotals } from '../api';
import { duration } from '../format';

export const ACTIVITY_LABEL: Record<TachoActivity, string> = { driving: 'Driving', work: 'Other work', available: 'Availability', rest: 'Break / rest', unknown: 'Unknown' };
const ORDER: TachoActivity[] = ['driving', 'work', 'available', 'rest', 'unknown'];
// Bar height per activity, so the chart reads without colour too.
const HEIGHT: Record<TachoActivity, number> = { driving: 1, work: 0.75, available: 0.5, rest: 0.25, unknown: 0.25 };

export const hm = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
export const hours = (min: number) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;
const weekday = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IE', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const utcTime = (iso: string) => iso.slice(11, 16);
const utcWhen = (iso: string) => new Date(iso).toLocaleString('en-IE', { timeZone: 'UTC', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const busy = (t: TachoTotals) => t.driving + t.work + t.available > 0;

/** A day's activities as a 24-hour bar chart: one row per slot that had a card (VU) or one row. */
function DayChart({ day, vu }: { day: TachoDay; vu: boolean }) {
  const rows = vu
    ? (['driver', 'co-driver'] as const).filter((s) => s === 'driver' || day.segments.some((x) => x.slot === s && x.cardInserted))
    : (['driver'] as const);
  const segsOf = (slot: string) => (vu ? day.segments.filter((s) => s.slot === slot) : day.segments);
  const H = 28;
  const title = (s: TachoSegment) => `${ACTIVITY_LABEL[s.kind]} ${hm(s.from)}–${hm(s.to)} (${duration(s.to - s.from)})`
    + (s.crew ? ', two drivers' : '') + (!s.cardInserted ? (vu ? ', no card in the slot' : s.kind === 'unknown' ? ', no data' : ', entered by hand') : '');
  return (
    <div className={`tacho-chart${vu ? ' slots' : ''}`}>
      {rows.map((slot) => (
        <div key={slot} className="tacho-chart-row">
          {vu && <span className="tacho-slot small muted">{slot === 'driver' ? 'Driver' : 'Co-driver'}</span>}
          <svg viewBox={`0 0 1440 ${H}`} preserveAspectRatio="none" role="img"
            aria-label={`${slot === 'driver' ? '' : 'Co-driver: '}${ORDER.filter((k) => segsOf(slot).some((s) => s.kind === k)).map((k) => ACTIVITY_LABEL[k]).join(', ') || 'no activity recorded'}`}>
            {[360, 720, 1080].map((x) => <line key={x} x1={x} x2={x} y1={0} y2={H} className="tacho-grid" />)}
            {segsOf(slot).map((s, i) => {
              const h = HEIGHT[s.kind] * H;
              return (
                <rect key={i} x={s.from} width={s.to - s.from} y={H - h} height={h} className={`tacho-seg tacho-${s.kind}${s.cardInserted ? '' : ' nocard'}`}>
                  <title>{title(s)}</title>
                </rect>
              );
            })}
          </svg>
        </div>
      ))}
    </div>
  );
}

function InfringementList({ list }: { list: TachoInfringement[] }) {
  return (
    <ul className="tacho-infs">
      {list.map((i, k) => (
        <li key={k}>
          <div className="tacho-inf-head">
            <strong>{utcTime(i.at)} · {i.title}</strong>
            {i.severity && <span className={`tacho-sev sev-${i.severity}`} title={i.severityLabel ?? undefined}>{i.severity} · {i.severityLabel}</span>}
          </div>
          <div>{i.detail}</div>
          <div className="muted small">
            {i.article}{i.from && i.to ? <> · checked {utcWhen(i.from)} – {utcWhen(i.to)} UTC</> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function TachoDays<D extends TachoDay>({ days, vu = false, infringements, period, extraNote }: {
  days: D[];
  vu?: boolean;
  infringements: (d: D) => TachoInfringement[];
  period: string;
  extraNote?: string;
}) {
  const [all, setAll] = useState(false);
  const shown = useMemo(() => [...days].reverse().filter((d) => all || busy(d.totals) || infringements(d).length > 0
    || (vu && d.coDriverTotals && busy(d.coDriverTotals))), [days, all, vu, infringements]);
  const quiet = days.length - shown.length;
  const sum = useMemo(() => days.reduce((a, d) => ({
    driving: a.driving + d.totals.driving, work: a.work + d.totals.work, km: a.km + (d.distanceKm ?? 0), infs: a.infs + infringements(d).length,
  }), { driving: 0, work: 0, km: 0, infs: 0 }), [days, infringements]);
  const noCardDriving = vu ? days.reduce((a, d) => a + d.segments.filter((s) => s.kind === 'driving' && !s.cardInserted && s.slot === 'driver').reduce((x, s) => x + s.to - s.from, 0), 0) : 0;

  return (
    <section className="card" aria-label="Daily activities">
      <div className="page-head">
        <h2>Daily activities</h2>
        <label className="small"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Show rest days too</label>
      </div>
      <p className="muted small">
        {days.length} days, {period} · driving {hours(sum.driving)}, other work {hours(sum.work)}
        {sum.km > 0 && <>, {sum.km.toLocaleString('en-IE')} km</>} · <span className={sum.infs ? 'tacho-count bad' : 'tacho-count'}>{sum.infs ? `${sum.infs} infringement${sum.infs === 1 ? '' : 's'}` : 'no infringements'}</span>.
        {' '}Times in UTC (tachograph time).
      </p>
      {noCardDriving > 0 && <p className="hint warn">Driving with no card in the driver slot: {duration(noCardDriving)} in this file (pale bars).</p>}
      {extraNote && <p className="muted small">{extraNote}</p>}
      <div className="tacho-legend small" aria-hidden="true">
        {ORDER.map((k) => <span key={k}><i className={`tacho-key tacho-${k}`} />{ACTIVITY_LABEL[k]}</span>)}
        <span className="muted">{vu ? 'Pale = no card in the slot' : 'Pale = card out or no data'}</span>
      </div>
      <div className={`tacho-axis small muted${vu ? ' slots' : ''}`} aria-hidden="true"><span>00</span><span>06</span><span>12</span><span>18</span><span>24</span></div>
      {shown.length === 0 && <p className="muted">{days.length ? 'Only rest days here.' : 'No activities.'}</p>}
      <ol className="tacho-days">
        {shown.map((d) => {
          const infs = infringements(d);
          const t = d.totals;
          return (
            <li key={d.date}>
              <div className="tacho-day-head">
                <strong>{weekday(d.date)}</strong>
                {d.noData && <span className="muted small">no file covers this day</span>}
              </div>
              <DayChart day={d} vu={vu} />
              <details className="tacho-sum">
                <summary>
                  <span className="tacho-totals small">
                    {(['driving', 'work', 'available', 'rest'] as const).map((k) => (
                      <span key={k} title={ACTIVITY_LABEL[k]}><i className={`tacho-key tacho-${k}`} aria-hidden="true" /><span className="sr-only">{ACTIVITY_LABEL[k]} </span>{hours(t[k])}</span>
                    ))}
                    {d.distanceKm !== null && <span className="muted">{d.distanceKm.toLocaleString('en-IE')} km</span>}
                  </span>
                  <span className={`tacho-count small${infs.length ? ' bad' : ''}`}>{infs.length ? `${infs.length} infringement${infs.length === 1 ? '' : 's'}` : 'No infringements'}</span>
                </summary>
                <div className="tacho-sum-body">
                  {infs.length ? <InfringementList list={infs} /> : <p className="muted small">Nothing found on this day.</p>}
                  {(d.vehicles?.length || d.cards?.length) ? (
                    <p className="small muted">
                      {d.vehicles?.length ? <>Vehicle {d.vehicles.join(', ')}</> : null}
                      {d.cards?.map((c, i) => (
                        <span key={i}>{i ? '; ' : ''}{c.slot === 'co-driver' ? 'Co-driver ' : ''}<b>{c.name}</b> {c.cardNumber}
                          {' '}({c.inserted?.slice(0, 10) === d.date ? `in ${c.inserted.slice(11, 16)}` : 'in before'}
                          {c.withdrawn ? (c.withdrawn.slice(0, 10) === d.date ? `, out ${c.withdrawn.slice(11, 16)}` : ', out later') : ', still in'})
                          {c.manualEntries ? ', entries by hand' : ''}</span>
                      ))}
                    </p>
                  ) : null}
                </div>
              </details>
            </li>
          );
        })}
      </ol>
      {quiet > 0 && !all && <p className="muted small">{quiet} rest {quiet === 1 ? 'day' : 'days'} hidden.</p>}
    </section>
  );
}
